import { Avatar, Box, List, Stack } from "@mui/material";
import { BadmintonMarkIcon } from "@bw/material";
import BoltRounded from "@mui/icons-material/BoltRounded";
import SchoolRounded from "@mui/icons-material/SchoolRounded";
import PersonRounded from "@mui/icons-material/PersonRounded";
import CardMembershipRounded from "@mui/icons-material/CardMembershipRounded";
import type { ReactNode } from "react";
import { DenseRow } from "../../../common/components/DenseRow";
import { SectionHeader } from "../../../common/components/SectionHeader";
import { withFacilityDiscoveryReturn } from "../../../common/facilityNavigation";

interface Offering {
  service: string;
  title: string;
  subtitle: string;
  action: string;
  to: string;
  icon: ReactNode;
}

/**
 * "Play here": every ready service as one dense row with its action.
 * Facility readiness is required; enabling a service alone is not an offering.
 */
export function FacilityOfferings({
  facilityId,
  enabledServices,
  readyServices = [],
  facilitySearchReturn,
}: {
  facilityId: string;
  enabledServices: string[];
  readyServices?: string[];
  facilitySearchReturn?: string;
}) {
  const id = encodeURIComponent(facilityId);
  const offerings: Offering[] = [
    { service: "court_reservation", title: "Courts", subtitle: "See court times and book", action: "Book", to: `/book/${id}`, icon: <BadmintonMarkIcon /> },
    { service: "open_play", title: "Drop-in", subtitle: "Open play sessions", action: "Join", to: `/dropin/${id}`, icon: <BoltRounded /> },
    { service: "classes", title: "Classes & clinics", subtitle: "Programs and group sessions", action: "View", to: `/classes/${id}`, icon: <SchoolRounded /> },
    { service: "coaching", title: "Private lessons", subtitle: "Coaches at this facility", action: "View", to: `/facility/${id}/coaches`, icon: <PersonRounded /> },
    { service: "memberships", title: "Memberships", subtitle: "Plans and passes", action: "View", to: `/facility/${id}/memberships`, icon: <CardMembershipRounded /> },
  ].filter((offering) => enabledServices.includes(offering.service) && readyServices.includes(offering.service));
  if (!offerings.length) return null;
  return (
    <Stack component="section" aria-labelledby="facility-play-title" spacing={0.5}>
      <SectionHeader id="facility-play-title" title="Play here" />
      <List disablePadding>
        {offerings.map((offering) => {
          const to = withFacilityDiscoveryReturn(offering.to, facilitySearchReturn);
          return (
            <DenseRow
              key={offering.service}
              to={to}
              leading={
                <Avatar variant="rounded" aria-hidden sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: "action.hover", color: "text.secondary" }}>
                  {offering.icon}
                </Avatar>
              }
              title={offering.title}
              subtitle={offering.subtitle}
              trailing={
                <Box
                  component="span"
                  sx={(theme) => ({
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 999,
                    fontSize: "0.8125rem",
                    fontWeight: 700,
                    flexShrink: 0,
                    border: 1,
                    borderColor: offering.action === "Book" ? "primary.main" : "divider",
                    bgcolor: offering.action === "Book" ? "primary.main" : "transparent",
                    color: offering.action === "Book" ? theme.palette.primary.contrastText : "text.primary",
                  })}
                >
                  {offering.action}
                </Box>
              }
            />
          );
        })}
      </List>
    </Stack>
  );
}
